import React from 'react';
import { Badge } from 'antd';
import { useFeatureDot } from 'utils/featureDots';
import { FEATURE_DOT_COLOR } from 'shared/config/featureDotsConfig';

interface FeatureDotBadgeProps {
  /** Dot ID from featureDotsConfig (must be in CURRENT_RELEASE_FEATURE_DOT_IDS). */
  dotId: string;
  children: React.ReactNode;
  /** Optional offset for the dot [horizontal, vertical] (Ant Design Badge offset). */
  offset?: [number, number];
  /** 
   * Optional: dismiss on click. If false, parent must call dismiss manually 
   * (e.g. via onOpenChange for Popover triggers). Default true.
   */
  dismissOnClick?: boolean;
}

/**
 * Wraps content with a red-dot badge when this feature dot is active for the
 * current release and not yet dismissed. 
 * 
 * By default, clicking the wrapped area dismisses the dot. Set dismissOnClick={false}
 * if the parent needs to control when dismiss happens (e.g. Popover onOpenChange).
 */
export const FeatureDotBadge: React.FC<FeatureDotBadgeProps> = ({
  dotId,
  children,
  offset = [2, 2],
  dismissOnClick = true,
}) => {
  const { showDot, dismiss } = useFeatureDot(dotId);

  if (!showDot) {
    return <>{children}</>;
  }

  if (!dismissOnClick) {
    // Just show badge, no wrapper onClick (color matches What's new)
    return (
      <Badge color={FEATURE_DOT_COLOR} dot offset={offset}>
        {children}
      </Badge>
    );
  }

  return (
    <Badge color={FEATURE_DOT_COLOR} dot offset={offset}>
      <span
        onClick={dismiss}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') dismiss();
        }}
        style={{ display: 'inline-block' }}
        role="presentation"
      >
        {children}
      </span>
    </Badge>
  );
};

/**
 * Hook to use feature dot state directly in components.
 * Use this when you need manual control over dismiss (e.g. in callbacks).
 */
export { useFeatureDot } from 'utils/featureDots';
